/*
 * Copyright (C) 2024 S&P Global.
 * All Rights Reserved
 */
import { AppIdentifier } from "@finos/fdc3";
import { AppType } from "../types";
import { AppReference, AppRegistrationListener } from "../types/lifecycle";

export class AppRefManager {
  private _browserGlobalScope: Window & typeof globalThis;
  private _appRefs: AppReference[] = [];
  private _appRegistrationListeners: AppRegistrationListener[] = [];

  constructor(browserGlobalScope: Window & typeof globalThis) {
    this._browserGlobalScope = browserGlobalScope;
    // console.log(`AppRefManager.constructor (${this._browserGlobalScope.location.origin})`);
  }

  public register(appType: AppType, appIdentifier: AppIdentifier, origin: string, window: WindowProxy): boolean {
    console.log(`AppRefManager.register (${this._browserGlobalScope.location.origin})`);
    if (!appIdentifier) {
      throw new Error("appIdentifier not provided");
    }
    if (!appIdentifier.appId) {
      throw new Error("appId is empty");
    }
    if (!appIdentifier.instanceId) {
      throw new Error("instanceId is empty");
    }
    if (!origin) {
      throw new Error("origin not provided");
    }

    let result = false;
    if (appType === AppType.parent && this._appRefs.some((ar) => ar.appType === AppType.parent)) {
      console.error(`Invalid attempted parent app registration (appId '${appIdentifier.appId}' and instanceId '${appIdentifier.instanceId}'). Only one parent app can be registered.`);
    }
    else if (this._appRefs.some((ar) => ar.appIdentifier.appId === appIdentifier.appId && ar.appIdentifier.instanceId === appIdentifier.instanceId)) {
      console.error(`App with appId '${appIdentifier.appId}' and instanceId '${appIdentifier.instanceId}' has already been registered.`);
    }
    else {
      const appRef: AppReference = {
        appType,
        appIdentifier,
        origin,
        window,
      };
      this._appRefs.push(appRef);
      console.log(`New appRefs (${this._browserGlobalScope.location.origin})`);
      console.log(Array.from(this._appRefs));
      this._appRegistrationListeners.forEach((listener) => listener(this._appRefs));
      result = true;
    }
    return result;
  }

  public unregister(appIdentifier: AppIdentifier, origin: string): boolean {
    console.log(`AppRefManager.unregister (${this._browserGlobalScope.location.origin})`);
    if (!appIdentifier) {
      throw new Error("appIdentifier not provided");
    }
    if (!appIdentifier.appId) {
      throw new Error("appId is empty");
    }
    if (!appIdentifier.instanceId) {
      throw new Error("instanceId is empty");
    }
    if (!origin) {
      throw new Error("origin not provided");
    }

    let result = false;
    if (!this._appRefs.find((ar) => ar.appIdentifier.appId === appIdentifier.appId && ar.appIdentifier.instanceId === appIdentifier.instanceId)) {
      console.error(`App with appId '${appIdentifier.appId}' and instanceId '${appIdentifier.instanceId}' was not found and cannot be unregistered.`);
    }
    else {
      this._appRefs = this._appRefs.filter((ar) => !(ar.appIdentifier.appId === appIdentifier.appId && ar.appIdentifier.instanceId === appIdentifier.instanceId));
      console.log("New appRefs");
      console.log(Array.from(this._appRefs));
      this._appRegistrationListeners.forEach((listener) => listener(this._appRefs));
      result = true;
    }
    return result;
  }

  public subscribe(appRegistrationListener: AppRegistrationListener): void {
    if (typeof appRegistrationListener !== "function") {
      throw new Error("appRegistrationListener is not a function");
    }
    this._appRegistrationListeners.push(appRegistrationListener);
  }

  public unsubscribe(appRegistrationListener: AppRegistrationListener): void {
    this._appRegistrationListeners = this._appRegistrationListeners.filter((listener) => listener !== appRegistrationListener);
  }

  public getAppReferences(appId: string): AppReference[] {
    console.log(`AppRefManager.getAppReferences (${this._browserGlobalScope.location.origin})`);
    return this._appRefs.filter((ar) => ar.appIdentifier.appId === appId);
  }

  public getAppReference(appIdentifier: AppIdentifier): AppReference | undefined {
    console.log(`AppRefManager.getAppReference (${this._browserGlobalScope.location.origin})`);
    if (!appIdentifier.instanceId) {
      throw new Error(`Failed to find appReference because no instanceId was specified for appId '${appIdentifier.appId}'`);
    }
    const appRef: AppReference | undefined = this._appRefs.find((ar) => ar.appIdentifier.appId === appIdentifier.appId && ar.appIdentifier.instanceId === appIdentifier.instanceId);
    if (!appRef) {
      console.error(`Failed to find appReference for appId '${appIdentifier.appId}' and instanceId '${appIdentifier.instanceId}'`);
    }
    return appRef;
  }

  public getFirstAppReference(appIdentifier: AppIdentifier): AppReference | undefined {
    console.log(`AppRefManager.getFirstAppReference (${this._browserGlobalScope.location.origin})`);
    let appRef: AppReference | undefined;
    if (appIdentifier.instanceId) {
      appRef = this.getAppReference(appIdentifier);
    }
    else {
      const appRefs = this.getAppReferences(appIdentifier.appId);
      if (appRefs.length > 0) {
        // Where multiple instances of the app are available, select the first one
        appRef = appRefs[0];
      }
    }
    return appRef;
  }

  public getAllAppIdentifiers(): AppIdentifier[] {
    console.log(`AppRefManager.getAllAppIdentifiers (${this._browserGlobalScope.location.origin})`);
    //TODO - This probably needs deduplicating
    return this._appRefs.map((ar) => ar.appIdentifier);
  }
}
